<template>
  <div class="atc-wrap">
    <button class="btn btn-outline-primary btn-sm atc-btn" type="button" @click="openGoogle">
      <i class="fas fa-calendar-plus mr-1"></i> Add to Google Calendar
    </button>
    <button class="btn btn-light btn-sm atc-btn" type="button" @click="downloadIcs">
      <i class="fas fa-file-download mr-1"></i> Download ICS
    </button>
  </div>
</template>

<script>
import { computed, toRefs, defineComponent } from 'vue'

export default defineComponent({
  name: 'AddToCalendar',
  props: {
    title: { type: String, default: 'Event' },
    description: { type: String, default: '' },
    location: { type: String, default: '' },
    start: { type: [String, Date], required: true }, // ISO string or Date
    end: { type: [String, Date], required: true },   // ISO string or Date
    url: { type: String, default: '' }
  },
  setup(props) {
    const { title, description, location, start, end, url } = toRefs(props)

    const startStr = computed(() => toDate(start.value))
    const endStr = computed(() => toDate(end.value))

    const formatIcsDate = d => {
      if (!d) return ''
      const iso = d.toISOString()
      return iso.replace(/[-:]/g, '').split('.')[0] + 'Z'
    }

    const googleHref = computed(() => {
      if (!startStr.value || !endStr.value) return '#'
      const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: title.value || 'Event',
        dates: `${formatIcsDate(startStr.value)}/${formatIcsDate(endStr.value)}`,
        details: description.value || '',
        location: location.value || '',
      })
      if (url.value) params.append('sprop', url.value)
      return `https://calendar.google.com/calendar/render?${params.toString()}`
    })

    function openGoogle() {
      if (googleHref.value === '#') return
      window.open(googleHref.value, '_blank', 'noopener,noreferrer')
    }

    function downloadIcs() {
      if (!startStr.value || !endStr.value) return
      const uid = `${Date.now()}@invi8`
      const ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Invi8//EN',
        'CALSCALE:GREGORIAN',
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `SUMMARY:${escapeText(title.value)}`,
        `DTSTART:${formatIcsDate(startStr.value)}`,
        `DTEND:${formatIcsDate(endStr.value)}`,
        description.value ? `DESCRIPTION:${escapeText(description.value)}` : '',
        location.value ? `LOCATION:${escapeText(location.value)}` : '',
        url.value ? `URL:${escapeText(url.value)}` : '',
        'END:VEVENT',
        'END:VCALENDAR'
      ].filter(Boolean).join('\r\n')

      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'event.ics'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    function escapeText(str) {
      return String(str || '').replace(/([,;])/g, '\\$1').replace(/\n/g, '\\n')
    }

    function toDate(val) {
      if (!val) return null
      if (val instanceof Date && !isNaN(val)) return val
      const raw = String(val).trim()
      if (!raw) return null
      const isoLike = raw.includes(' ') ? raw.replace(' ', 'T') : raw
      const normalized = /^\d{4}-\d{2}-\d{2}$/.test(isoLike) ? `${isoLike}T00:00:00` : isoLike
      const d = new Date(normalized)
      return isNaN(d) ? null : d
    }

    return {
      googleHref,
      openGoogle,
      downloadIcs
    }
  }
})
</script>

<style scoped>
.atc-wrap{display:flex;flex-wrap:wrap;gap:.5rem;}
.atc-btn{border-radius:999px;font-weight:700;}
</style>
